<template>
  <div class="animated-logo-cloud">
    <!-- Loading State with MotionLoader -->
    <div v-if="isLoading" class="flex justify-center items-center min-h-[200px]">
      <MotionLoader
        :variant="loaderVariant || 'dots'"
        :size="loaderSize || 'md'"
        color="text-gray-400"
        :show-text="true"
        loading-text="Loading logos..."
      />
    </div>

    <!-- Error State -->
    <motion.div
      v-else-if="hasError"
      :initial="{ opacity: 0, scale: 0.8 }"
      :animate="{ opacity: 1, scale: 1 }"
      :transition="{ type: 'spring', stiffness: 400, damping: 25 }"
      class="flex justify-center items-center  p-8 text-center text-red-500"
      style="min-height: 200px;"
    >
      <div>
        <i class="fas fa-exclamation-triangle text-4xl mb-4"></i>
        <p class="text-lg font-semibold">Failed to load logos</p>
        <p class="text-sm text-gray-500 mt-2">Please try again later</p>
      </div>
    </motion.div>

    <!-- Animated Logo Cloud -->
    <motion.div
      v-else-if="logos && logos.length > 0"
      :initial="animationVariants.initial"
      :animate="animationVariants.animate"
      :transition="animationVariants.transition"
      class="logo-cloud-container"
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave"
    >
      <!-- Title -->
      <motion.div
        v-if="title"
        :initial="{ opacity: 0, y: 20 }"
        :animate="{ opacity: 1, y: 0 }"
        :transition="{ delay: 0.2, duration: 0.6 }"
        class="text-center mb-8"
      >
        <h2 class="text-2xl font-bold text-gray-800 mb-2">{{ title }}</h2>
      </motion.div>

      <!-- Logo Cloud Animation Container -->
      <div class="relative" style="overflow-x: hidden; overflow-y: visible;">
        <!-- Left Fade Overlay -->
        <div 
          class="absolute left-0 top-0 w-32 h-full z-10 pointer-events-none fade-overlay-left"
          :style="{ '--fade-rgb': fadeColorRgb }"
        ></div>
        
        <!-- Right Fade Overlay -->
        <div 
          class="absolute right-0 top-0 w-32 h-full z-10 pointer-events-none fade-overlay-right"
          :style="{ '--fade-rgb': fadeColorRgb }"
        ></div>
        
        <!-- First Row -->
        <div
          ref="scrollContainer1"
          class="flex space-x-2 mb-6 scroll-container"
          :style="{ 
            '--animation-duration': animationSpeed + 's',
            transform: `translateX(${scrollOffset1}px)`,
            overflow: 'visible'
          }"
        >
          <div
            v-for="(logo, index) in [...Array(3)].flatMap(() => logos)"
            :key="`row1-${index}`"
            class="flex-shrink-0 h-16 w-32 flex items-center justify-center relative overflow-visible p-1"
          >
            <component
              :is="logo.website ? 'a' : 'div'"
              :href="logo.website"
              :target="logo.website ? '_blank' : undefined"
              :rel="logo.website ? 'noopener noreferrer' : undefined"
              class="flex items-center justify-center w-full h-full transition-all duration-300 rounded-lg hover:border-2 hover:scale-110 logo-container group transform"
              :class="{ 'cursor-pointer': logo.website }"
              :style="logo.primaryColor ? `--hover-bg: ${logo.primaryColor}15; --hover-border: ${logo.primaryColor}` : '--hover-bg: rgba(0, 0, 0, 0.05); --hover-border: #e5e7eb'"
            >
              <img
                :src="logo.path"
                :alt="logo.name"
                class="max-h-full max-w-full object-contain transition-all duration-300 p-1 bg-white rounded-md logo-image"
                :style="{
                  filter: colorOnHover ? 'grayscale(1)' : 'none'
                }"
                :title="logo.name"
                @error="handleImageError"
              />
            </component>
          </div>
        </div>

        <!-- Second Row (Reverse Direction) -->
        <div
          ref="scrollContainer2"
          class="flex space-x-2 scroll-container"
          :style="{ 
            '--animation-duration': (animationSpeed * 1.2) + 's',
            transform: `translateX(${scrollOffset2}px)`,
            overflow: 'visible'
          }"
        >
          <div
            v-for="(logo, index) in [...Array(3)].flatMap(() => logos.slice().reverse())"
            :key="`row2-${index}`"
            class="flex-shrink-0 h-16 w-32 flex items-center justify-center relative overflow-visible p-2"
          >
            <component
              :is="logo.website ? 'a' : 'div'"
              :href="logo.website"
              :target="logo.website ? '_blank' : undefined"
              :rel="logo.website ? 'noopener noreferrer' : undefined"
              class="flex items-center justify-center w-full h-full transition-all duration-300 rounded-lg hover:border-2 hover:scale-110 logo-container group transform"
              
              :class="{ 'cursor-pointer': logo.website }"
              :style="logo.primaryColor ? `--hover-bg: ${logo.primaryColor}15; --hover-border: ${logo.primaryColor}` : '--hover-bg: rgba(0, 0, 0, 0.05); --hover-border: #e5e7eb'"
            >
              <img
                :src="logo.path"
                :alt="logo.name"
                class="max-h-full max-w-full object-contain transition-all duration-300 p-2 bg-white rounded-md logo-image"
                :style="{
                  filter: colorOnHover ? 'grayscale(1)' : 'none'
                }"
                :title="logo.name"
                @error="handleImageError"
              />
            </component>
          </div>
        </div>
      </div>

      <!-- Stats or Additional Info -->
      <motion.div
        v-if="showStats"
        :initial="{ opacity: 0, y: 20 }"
        :animate="{ opacity: 1, y: 0 }"
        :transition="{ delay: 0.8, duration: 0.6 }"
        class="text-center mt-8 text-sm text-gray-500"
      >
   
      </motion.div>
    </motion.div>

    <!-- Empty State -->
    <motion.div
      v-else
      :initial="{ opacity: 0, scale: 0.8 }"
      :animate="{ opacity: 1, scale: 1 }"
      :transition="{ type: 'spring', stiffness: 400, damping: 25 }"
      class="flex justify-center items-center min-h-[200px] p-8 text-center text-gray-500"
    >
      <div>
        <i class="fas fa-image text-4xl mb-4"></i>
        <p class="text-lg">No logos available</p>
      </div>
    </motion.div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import { motion } from 'motion-v';
import WidgetMixin from '../../Mixins/WidgetMixin';
import MotionLoader from './MotionLoader.vue';
import axios from 'axios';

export default defineComponent({
  name: 'AnimatedLogoCloud',
  mixins: [WidgetMixin],
  components: {
    motion,
    MotionLoader
  },
  props: {
    title: {
      type: String,
      default: ''
    },
    animationSpeed: {
      type: Number,
      default: 100
    },
    showStats: {
      type: Boolean,
      default: true
    },
    customEndpoint: {
      type: String,
      default: null
    },
    colorOnHover: {
      type: Boolean,
      default: true
    },
    fadeColor: {
      type: String,
      default: '#ffffff'
    }
  },
  data() {
    return {
      logos: [],
      hasError: false,
      scrollOffset1: 0,
      scrollOffset2: 0,
      animationFrame: null,
      isPaused: false
    };
  },
  computed: {
    animationVariants() {
      return {
        initial: { 
          opacity: 0, 
          y: 30, 
          scale: 0.95,
          filter: 'blur(10px)'
        },
        animate: { 
          opacity: 1, 
          y: 0, 
          scale: 1,
          filter: 'blur(0px)'
        },
        transition: {
          type: 'spring',
          stiffness: 300,
          damping: 30,
          opacity: { duration: 0.6 },
          filter: { duration: 0.6 }
        }
      };
    },
    fadeColorRgb() {
      // Convert hex color to RGB format for CSS custom properties
      const hex = this.fadeColor.replace('#', '');
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      return `${r}, ${g}, ${b}`;
    }
  },
  methods: {
    async getLogos() {
      const lang = window.appSettings?.appLanguage || 'de';
      const endpoint = this.customEndpoint 
        ? this.customEndpoint
        : this.url 
        ? this.url
        : `https://${this.domain}/api/customer/widget/${this.id}/${this.hash}`;

      // Set loading state
      this.isLoading = true;
      this.hasError = false;
      const loadingStartTime = Date.now();

      try {
        const { data } = await axios.get(endpoint, {
          params: {
            currentUrl: window.location.href,
            lang
          }
        });

        console.log('Raw API response:', data); // Debug log

        // Handle different response formats
        let processedLogos = [];

        if (Array.isArray(data)) {
          // Direct array of company objects
          processedLogos = this.processCompanyData(data);
        } else if (data.logos && Array.isArray(data.logos)) {
          // Nested in logos property
          processedLogos = this.processCompanyData(data.logos);
        } else if (data.companies && Array.isArray(data.companies)) {
          // Alternative nested structure
          processedLogos = this.processCompanyData(data.companies);
        } else if (data.html) {
          // If HTML is returned, try to extract logos from it
          this.html = data.html;
          this.extractLogosFromHtml();
          processedLogos = this.logos;
        } else {
          // Single company object
          if (data.name || data.logo) {
            processedLogos = this.processCompanyData([data]);
          } else {
            // Fallback to demo data if no logos found
            this.setDemoLogos();
            processedLogos = this.logos;
          }
        }

        if (processedLogos.length > 0) {
          this.logos = processedLogos;
        } else {
          // Fallback to demo data if processing resulted in empty array
          this.setDemoLogos();
        }

        // Process CSS if provided
        if (data.css_url) {
          const link = document.createElement('link');
          link.rel = 'stylesheet';
          link.href = data.css_url;
          document.head.appendChild(link);
        }

        // Ensure loader shows for at least 600ms for better UX
        const elapsedTime = Date.now() - loadingStartTime;
        const minDisplayTime = 600;
        const remainingTime = Math.max(0, minDisplayTime - elapsedTime);

        setTimeout(() => {
          this.isLoading = false;
        }, remainingTime);

      } catch (err) {
        console.error('Error loading logos:', err);
        this.hasError = true;
        this.isLoading = false;
        
        // Fallback to demo data on error
        setTimeout(() => {
          this.hasError = false;
          this.setDemoLogos();
        }, 2000);
      }
    },

    processCompanyData(companies) {
      // Process the specific JSON structure from your API
      return companies
        .map(company => {
          // Extract company name
          let name = 'Company';
          if (company.name) {
            if (typeof company.name === 'string') {
              name = company.name;
            } else if (company.name.A0) {
              name = company.name.A0;
            } else if (company.name.de || company.name.en) {
              // Handle multilingual names
              const lang = window.appSettings?.appLanguage || 'de';
              name = company.name[lang] || company.name.de || company.name.en || Object.values(company.name)[0];
            }
          }

          // Extract logo URL - prefer oar-md, fallback to md, then other sizes
          let logoPath = null;
          if (company.logo && !company.logo.default) {
            // Priority order for logo selection
            const logoSizes = ['oar-md', 'md', 'oar-lg', 'lg', 'oar-sm', 'sm'];
            for (const size of logoSizes) {
              if (company.logo[size]) {
                logoPath = company.logo[size];
                break;
              }
            }
          } else if (typeof company.logo === 'string') {
            // Direct logo URL
            logoPath = company.logo;
          } else if (company.path) {
            // Fallback to path property
            logoPath = company.path;
          }

          // Only include companies with valid logos
          if (!logoPath) {
            return null;
          }

          // Build the company object with additional metadata
          const processedCompany = {
            name: name,
            path: logoPath
          };

          // Add optional metadata for enhanced features
          if (company.color1) {
            processedCompany.primaryColor = company.color1;
          }
          if (company.color2) {
            processedCompany.secondaryColor = company.color2;
          }
          if (company.website) {
            if (typeof company.website === 'string') {
              processedCompany.website = company.website;
            } else if (company.website.A0) {
              processedCompany.website = company.website.A0;
            }
          }

          return processedCompany;
        })
        .filter(company => company !== null); // Remove invalid entries
    },

    extractLogosFromHtml() {
      // Try to extract logo data from HTML if it contains structured data
      try {
        if (this.html) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(this.html, 'text/html');
          const logoElements = doc.querySelectorAll('[data-logo]');
          
          const extractedLogos = Array.from(logoElements).map(el => ({
            name: el.getAttribute('data-name') || el.getAttribute('alt') || 'Company',
            path: el.getAttribute('data-logo') || el.getAttribute('src')
          })).filter(logo => logo.path);

          if (extractedLogos.length > 0) {
            this.logos = extractedLogos;
          } else {
            this.setDemoLogos();
          }
        }
      } catch (error) {
        console.error('Error extracting logos from HTML:', error);
        this.setDemoLogos();
      }
    },

    setDemoLogos() {
      // Demo logos as fallback
      this.logos = [
        {
          name: "Vercel",
          path: "https://res.cloudinary.com/dfhp33ufc/image/upload/v1715881430/vercel_wordmark_dark_mhv8u8.svg",
        },
        {
          name: "Prime",
          path: "https://res.cloudinary.com/dfhp33ufc/image/upload/v1715276558/logos/t2awrrfzdvmg1chnzyfr.svg",
        },
        {
          name: "Trustpilot",
          path: "https://res.cloudinary.com/dfhp33ufc/image/upload/v1715276558/logos/tkfspxqmjflfllbuqxsi.svg",
        },
        {
          name: "Webflow",
          path: "https://res.cloudinary.com/dfhp33ufc/image/upload/v1715276560/logos/nymiivu48d5lywhf9rpf.svg",
        },
        {
          name: "Airbnb",
          path: "https://res.cloudinary.com/dfhp33ufc/image/upload/v1715276558/logos/pmblusboe7vkw8vxdknx.svg",
        },
        {
          name: "Tina",
          path: "https://res.cloudinary.com/dfhp33ufc/image/upload/v1715276560/logos/afqhiygywyphuou6xtxc.svg",
        },
        {
          name: "Stackoverflow",
          path: "https://res.cloudinary.com/dfhp33ufc/image/upload/v1715276558/logos/ts1j4mkooxqmscgptafa.svg",
        },
        {
          name: "Mistral",
          path: "https://res.cloudinary.com/dfhp33ufc/image/upload/v1715276558/logos/tyos2ayezryjskox3wzs.svg",
        }
      ];
    },

    handleImageError(event) {
      // Hide broken images gracefully
      event.target.style.display = 'none';
    },

    async refreshLogos() {
      await this.getLogos();
    },

    startInfiniteScroll() {
      let lastTime = 0;
      let logoWidth = 0;
      let setWidth = 0;
      let initialized = false;

      const animate = (currentTime) => {
        if (!lastTime) lastTime = currentTime;
        const deltaTime = currentTime - lastTime;
        lastTime = currentTime;

        // Calculate logo and set width dynamically
        if (!logoWidth && this.$refs.scrollContainer1) {
          const logoElement = this.$refs.scrollContainer1.querySelector('.flex-shrink-0');
          if (logoElement) {
            logoWidth = logoElement.offsetWidth + 32; // width + gap
            setWidth = logoWidth * this.logos.length;
            
            // Initialize the second row to start from the left (negative position)
            if (!initialized) {
              this.scrollOffset2 = -setWidth;
              initialized = true;
            }
          }
        }

        // Only animate if not paused
        if (!this.isPaused && logoWidth && setWidth) {
          // Calculate speed based on animation duration
          const speed1 = setWidth / (this.animationSpeed * 1000); // pixels per ms
          const speed2 = setWidth / (this.animationSpeed * 1.2 * 1000); // slower for second row

          // Update scroll positions
          // Row 1: moves left (negative direction)
          this.scrollOffset1 -= speed1 * deltaTime;
          // Row 2: moves right (positive direction)
          this.scrollOffset2 += speed2 * deltaTime;

          // Reset when one complete set has scrolled
          // Row 1: reset when it goes too far left
          if (this.scrollOffset1 <= -setWidth) {
            this.scrollOffset1 += setWidth;
          }
          // Row 2: reset when it goes too far right
          if (this.scrollOffset2 >= 0) {
            this.scrollOffset2 = -setWidth;
          }
        }

        this.animationFrame = requestAnimationFrame(animate);
      };

      this.animationFrame = requestAnimationFrame(animate);
    },

    stopInfiniteScroll() {
      if (this.animationFrame) {
        cancelAnimationFrame(this.animationFrame);
        this.animationFrame = null;
      }
    },

    handleMouseEnter() {
      this.isPaused = true;
    },

    handleMouseLeave() {
      this.isPaused = false;
    }
  },

  async mounted() {
    // Override the WidgetMixin's getHtml with our custom getLogos method
    await this.getLogos();
    this.htmlLoadedCustom();
    
    // Start infinite scroll after logos are loaded
    this.$nextTick(() => {
      this.startInfiniteScroll();
    });
  },

  beforeUnmount() {
    this.stopInfiniteScroll();
  }
});
</script>

<style scoped>
.animated-logo-cloud {
  @apply w-full py-8;
}

.logo-cloud-container {
  @apply max-w-6xl mx-auto px-4;
}

/* Fade overlays for smooth edge transitions */
.fade-overlay-left,
.fade-overlay-right {
  backdrop-filter: blur(1px);
  --fade-rgb: 255, 255, 255; /* Default white, can be overridden */
}

.fade-overlay-left {
  background: linear-gradient(
    to right,
    rgba(var(--fade-rgb), 1) 0%,
    rgba(var(--fade-rgb), 0.95) 15%,
    rgba(var(--fade-rgb), 0.8) 35%,
    rgba(var(--fade-rgb), 0.5) 55%,
    rgba(var(--fade-rgb), 0.2) 75%,
    rgba(var(--fade-rgb), 0) 100%
  );
}

.fade-overlay-right {
  background: linear-gradient(
    to left,
    rgba(var(--fade-rgb), 1) 0%,
    rgba(var(--fade-rgb), 0.95) 15%,
    rgba(var(--fade-rgb), 0.8) 35%,
    rgba(var(--fade-rgb), 0.5) 55%,
    rgba(var(--fade-rgb), 0.2) 75%,
    rgba(var(--fade-rgb), 0) 100%
  );
}

/* Enhanced logo fade effect when passing through overlays */
.logo-cloud-container {
  mask: linear-gradient(
    to right,
    transparent 0%,
    rgba(0, 0, 0, 0.1) 3%,
    rgba(0, 0, 0, 0.5) 8%,
    black 15%,
    black 85%,
    rgba(0, 0, 0, 0.5) 92%,
    rgba(0, 0, 0, 0.1) 97%,
    transparent 100%
  );
  -webkit-mask: linear-gradient(
    to right,
    transparent 0%,
    rgba(0, 0, 0, 0.1) 3%,
    rgba(0, 0, 0, 0.5) 8%,
    black 15%,
    black 85%,
    rgba(0, 0, 0, 0.5) 92%,
    rgba(0, 0, 0, 0.1) 97%,
    transparent 100%
  );
}

/* Logo container styling */
.logo-container {
  background: transparent;
  border: 2px solid transparent;
  position: relative;
  z-index: 1;
}

.logo-container:hover {
  z-index: 10;
  background-color: var(--hover-bg);
  border-color: var(--hover-border);
  transform: translateY(-1px);
}

/* Smooth animations and transitions */
.logo-cloud-container img {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.logo-container:hover img {
  filter: grayscale(0%) !important;
}

/* Ensure overflow is visible for scaling */
.scroll-container {
  overflow: visible !important;
}

/* Logo spacing and overflow handling */
.logo-cloud-container {
  padding: 20px 0; /* Add vertical padding to accommodate scaled logos */
}

/* Enhanced hover effects for clickable logos */
.logo-cloud-container a:hover {
  text-decoration: none;
}
</style> 